import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { quantityValidator } from '../../shared/services/validations';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { VendorInvoiceService } from '../../features/vendor-invoice/services/vendor-invoice.service';
import { PreferenceCodeService } from '../../features/preference-code/services/preference-code.service';

@Component({
  selector: 'app-vendor-invoice-new',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    ReactiveFormsModule,
    FormsModule,
    NgxMatSelectSearchModule,
    MatDatepickerModule,
  ],
  templateUrl: './vendor-invoice-new.component.html',
  styleUrl: './vendor-invoice-new.component.scss',
})
export class VendorInvoiceNewComponent {
  newInvoiceForm: FormGroup;
  vendorData: any = {};
  invoiceId: any;
  validationError: boolean = false;
  documentArray: any[] = [];
  public genderData: any[] = [];
  public preferenceData: any[] = [];
  today: Date = new Date();
  currencyData: any;
  billData: any;
  billNo: any;
  overallSubTotal: number = 0;
  overallVat: number = 0;
  overallTotalAmount: number = 0;
  totalAmount: number = 0;
  currencyCode: String = 'AED';
  exchangeRate: number = 0;



  customers: any[] = [];
  vendors: any[] = [];
  itemsdata: any[] = [];
  accountsData: any[] = [];
  purchaseOrderData: any[] = [];
  private searchSubject = new Subject<string>();
  private accountsSearchSubject = new Subject<string>();
  private purchaseOrderSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private vendorInvoiceService: VendorInvoiceService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private preferenceService: PreferenceCodeService
  ) {}

  ngOnInit() {
    this.initForm();
    this.getVendors();
    this.getACcounts();
    this.route.paramMap.subscribe((params) => {
      this.invoiceId = params.get('id');
      if (this.invoiceId) {
        this.fetchVendorInvoiceById(this.invoiceId);
      }
    });
    if (!this.invoiceId) {
      this.addItem();
      this.preferenceCode();
    }
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getVendors(searchTerm);
      });

    this.accountsSearchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getACcounts(searchTerm);
      });

    this.newInvoiceForm.get('vendorId')?.valueChanges.subscribe((vendorId) => {
      if (vendorId) {
        this.newInvoiceForm.patchValue({
          purchaseOrderId: null,
        });

        const itemsArray = this.newInvoiceForm.get('items') as FormArray;
        while (itemsArray.length !== 0) {
          itemsArray.removeAt(0);
        }
        this.addItem();
        this.updateOverallSummary();
        this.purchaseOrdersByVendorId('', vendorId);
      }
    });

    this.purchaseOrderSearchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        const vendorId = this.newInvoiceForm.get('vendorId')?.value;
        if (vendorId) {
          this.purchaseOrdersByVendorId(searchTerm, vendorId);
        }
      });

    this.newInvoiceForm
      .get('purchaseOrderId')
      ?.valueChanges.subscribe((poId) => {
        if (poId) {
          const itemsArray = this.newInvoiceForm.get('items') as FormArray;
          while (itemsArray.length !== 0) {
            itemsArray.removeAt(0);
          }
    
          this.patchupItems(poId);
        }
      });
  }

  initForm() {
    if (this.billData) {
      this.newInvoiceForm = this.fb.group({
        id: Number(this.invoiceId),
        billNo: [this.billData?.billNo || '', [Validators.required]],
        vendorId: [this.billData?.vendorId || null, [Validators.required]],
        accountId: [this.billData?.accountId || null, [Validators.required]],
        purchaseOrderId: [
          this.billData?.purchaseOrderId || null,
          [Validators.required],
        ],
        dueDate: [
          this.billData?.dueDate ? new Date(this.billData.dueDate) : '',
          [Validators.required],
        ],
        items: this.fb.array([]),
      });

      // Patch item list
      if (this.billData.billItems?.length) {
        const itemsArray = this.newInvoiceForm.get('items') as FormArray;
        this.billData.billItems.forEach((item: any) => {
          const itemGroup = this.fb.group({
            itemId: [item.id],
            itemName: [item.itemName, Validators.required],
            description: [item.description],
            quantity: [
              item.quantity,
              [
                Validators.required,
                Validators.pattern(/^\d+(\.\d+)?$/),
                quantityValidator,
              ],
            ],
            rate: [item.rate, Validators.required],
            subTotal: [item.subTotal],
            tax: [item.tax],
            totalAmount: [item.totalAmount],
          });

          itemGroup.valueChanges.subscribe((value) => {
            const quantity = parseFloat(value.quantity) || 0;
            const rate = parseFloat(value.rate) || 0;
            const subTotal = quantity * rate;
            const tax = subTotal * 0.05;
            const totalAmount = subTotal + tax;

            itemGroup.patchValue(
              {
                subTotal,
                tax,
                totalAmount,
              },
              { emitEvent: false }
            );

            this.updateOverallSummary();
          });

          itemsArray.push(itemGroup);
        });

        this.updateOverallSummary();
      }

      this.newInvoiceForm
        .get('vendorId')
        ?.valueChanges.subscribe((vendorId) => {
          if (vendorId) {
            this.newInvoiceForm.patchValue({
              purchaseOrderId: null,
            });

            const itemsArray = this.newInvoiceForm.get('items') as FormArray;
            while (itemsArray.length !== 0) {
              itemsArray.removeAt(0);
            }
            this.addItem();
            this.updateOverallSummary();
            this.purchaseOrdersByVendorId('', vendorId);
          }
        });

      this.newInvoiceForm
        .get('purchaseOrderId')
        ?.valueChanges.subscribe((poId) => {
          if (poId) {
            this.patchupItems(poId);
          }
        });
    } else {
      this.newInvoiceForm = this.fb.group({
        billNo: ['', [Validators.required]],
        vendorId: [null, [Validators.required]],
        accountId: [null, [Validators.required]],
        purchaseOrderId: [null, [Validators.required]],
        dueDate: ['', [Validators.required]],
        items: this.fb.array([]),
      });
    }
  }

  preferenceCode(search: string = '') {
    this.preferenceService.fetchPreferenceCodes(search, 'Manual Journal').subscribe({
      next: (response) => {
        response.forEach((prefix: any) => {
          this.billNo = prefix?.sequenceCode;
        });
        this.newInvoiceForm.patchValue({ billNo: this.billNo });
      },
      error: (error) => {
        console.error(error, 'Error');
      },
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  onSearchAccounts(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.accountsSearchSubject.next(searchTerm);
  }

  onSearchPurchaseOrders(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.purchaseOrderSearchSubject.next(searchTerm);
  }

  getVendors(search: string = '') {
    this.vendorInvoiceService.fetchVendors(search).subscribe({
      next: (data) => {
        // console.log(data);
        this.vendors = data;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getACcounts(search: string = '', accountType: any = 'Expense') {
    this.vendorInvoiceService.fetchAccounts(accountType, search).subscribe({
      next: (data) => {
        // console.log(data);
        this.accountsData = data;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  purchaseOrdersByVendorId(
    search: string = '',
    vendorId: any,
    callback?: () => void
  ) {
    const input = {
      search: search || '',
      filter: {
        vendorId: vendorId,
        isApproved: true,
      },
    };
    this.vendorInvoiceService.purchaseOrders(input).subscribe({
      next: (data) => {
        this.purchaseOrderData = data?.purchaseOrders;
        // this.itemsdata = data?.purchaseOrders?.purchaseItems;
        if (callback) callback();
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  patchupItems(poId: any) {
    const purchaseOrders = this.purchaseOrderData || [];
    const selectedPO = purchaseOrders.find((po: any) => po.id === poId);
    this.currencyCode = selectedPO?.currencyData?.currencyCode;

    if (selectedPO) {
      const itemsArray = this.newInvoiceForm.get('items') as FormArray;
      if (selectedPO?.purchaseItems.length !== 0) {
        this.onRemoveNewlyAddedItems(0);
      }
      selectedPO?.purchaseItems?.forEach((item: any) => {
        const itemGroup = this.fb.group({
          itemId: [item.id],
          itemName: [item.itemName, Validators.required],
          description: [item.description],
          quantity: [
            item.quantity,
            [
              Validators.required,
              Validators.pattern(/^\d+(\.\d+)?$/),
              quantityValidator,
            ],
          ],
          rate: [item.rate, Validators.required],
          subTotal: [item.subTotal],
          tax: [item.tax],
          totalAmount: [item.totalAmount],
        });

        itemGroup.valueChanges.subscribe((value) => {
          const quantity = parseFloat(value.quantity) || 0;
          const rate = parseFloat(value.rate) || 0;
          const subTotal = quantity * rate;
          const tax = subTotal * 0.05;
          const totalAmount = subTotal + tax;

          itemGroup.patchValue(
            {
              subTotal,
              tax,
              totalAmount,
            },
            { emitEvent: false }
          );

          this.updateOverallSummary();
        });

        itemsArray.push(itemGroup);
      });

      this.updateOverallSummary();
    }
  }

  allowOnlyNumberWithDecimal(event: KeyboardEvent): void {
    const inputChar = String.fromCharCode(event.keyCode || event.which);

    // Allow digits and decimal point
    if (!/^[0-9.]$/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    // Disallow more than one decimal point
    const input = event.target as HTMLInputElement;
    if (inputChar === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }

  blockPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    if (!/^\d*\.?\d*$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  allowOnlyInteger(event: KeyboardEvent): void {
    const inputChar = String.fromCharCode(event.keyCode || event.which);

    // Allow only digits 0-9
    if (!/^[0-9]$/.test(inputChar)) {
      event.preventDefault();
    }
  }

  blockPasteNonInteger(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    // Allow only digits, no decimal or special chars
    if (!/^\d+$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  fetchVendorInvoiceById(id: any) {
    this.vendorInvoiceService.listBillById(Number(id)).subscribe({
      next: (data) => {
        this.billData = data;
        this.purchaseOrdersByVendorId('', data.customerId, () => {
          this.initForm();
        });
        // this.initForm();
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  get items(): FormArray {
    return this.newInvoiceForm.get('items') as FormArray;
  }

  onRemoveNewlyAddedItems(index: number): void {
    this.items.removeAt(index);
    this.updateOverallSummary();
  }

  addItem(): void {
    const itemGroup = this.fb.group({
      itemId: [null],
      itemName: ['', Validators.required],
      description: [''],
      quantity: [
        1,
        [
          Validators.required,
          Validators.pattern(/^\d+(\.\d+)?$/),
          quantityValidator,
        ],
      ],
      rate: [0, Validators.required],
      subTotal: [0],
      tax: [0],
      totalAmount: [0],
    });

    this.items.push(itemGroup);

    // Auto calculate on change
    itemGroup.valueChanges.subscribe((value) => {
      const quantity = parseFloat((value.quantity ?? '0').toString()) || 0;
      const rate = parseFloat((value.rate ?? '0').toString()) || 0;
      // const quantity = parseFloat(value.quantity) || 0;
      // const rate = parseFloat(value.rate) || 0;
      const subTotal = quantity * rate;
      const tax = subTotal * 0.05;
      const totalAmount = subTotal + tax;

      itemGroup.patchValue(
        {
          subTotal,
          tax,
          totalAmount,
        },
        { emitEvent: false }
      );

      this.updateOverallSummary();
    });
  }

  updateOverallSummary(): void {
    const items = this.newInvoiceForm.get('items') as FormArray;
    let subTotal = 0;
    let tax = 0;

    items.controls.forEach((item) => {
      subTotal += parseFloat(item.get('subTotal')?.value) || 0;
      tax += parseFloat(item.get('tax')?.value) || 0;
    });

    const totalAmount = subTotal + tax;

    this.overallSubTotal = subTotal;
    this.overallVat = tax;
    this.overallTotalAmount = totalAmount;
    // if(this.currencyCode !== 'AED'){
    //   this.totalAmount = totalAmount;
    //   this.overallTotalAmount = totalAmount / this.exchangeRate;
    // }
  }

  async onSubmit(status: 'Draft' | 'Pending' = 'Draft') {
    if (this.newInvoiceForm.invalid) {
      this.newInvoiceForm.markAllAsTouched();
      return;
    }

    const formValue = this.newInvoiceForm.value;

    if (!formValue.items || formValue.items.length === 0) {
      this.toastr.error(
        'Please add at least one item before submitting the invoice.'
      );
      return;
    }

    const createBillInput = {
      billNo: formValue.billNo,
      vendorId: formValue.vendorId,
      accountId: formValue.accountId,
      subTotal: this.overallSubTotal,
      tax: this.overallVat,
      totalAmount: this.overallTotalAmount,
      billStatus: status, // either 'Draft' or 'Pending'
      purchaseOrderId: formValue.purchaseOrderId,
      billDate: new Date().toISOString(),
      dueDate: new Date(formValue.dueDate).toISOString(),
      items: formValue.items.map((item: any) => ({
        itemName: item.itemName,
        description: item.description,
        quantity: Number(item.quantity),
        rate: Number(item.rate),
        tax: Number(item.tax),
        subTotal: Number(item.subTotal),
        totalAmount: Number(item.totalAmount),
      })),
    };

    if (!this.invoiceId) {
      this.vendorInvoiceService.createNewBill(createBillInput).subscribe({
        next: async (data) => {
          this.toastr.success('Bill created successfully', 'Success');
          this.onCancel();
          this.router.navigate([`/vendor-invoice`]);
        },
        error: (error) => this.toastr.error(error, 'Error'),
      });
    } else {
      this.vendorInvoiceService
        .updateBillById(createBillInput, Number(this.invoiceId))
        .subscribe({
          next: async (data) => {
            this.toastr.success('Bill updated successfully', 'Success');
            this.onCancel();
            this.router.navigate([`/vendor-invoice`]);
          },
          error: (error) => this.toastr.error(error, 'Error'),
        });
    }
  }

  onCancel() {
    if (!this.invoiceId) {
      this.router.navigate([`/vendor-invoice`]);
    } else {
      this.router.navigate([`/vendor-invoice`]);
    }
  }
}
